import React from 'react';
import { useNavigate } from 'react-router-dom';
import SignalBadge from './SignalBadge';
import { useRealtimePrice } from '../hooks/useRealtimePrice';
import { SignalType, SignalStrength } from '../services/api';

interface SignalRowProps {
  symbol: string;
  price: number;
  change_percent: number;
  previousClose?: number;
  signal: SignalType;
  strength: SignalStrength;
  confidence: number;
  size?: 'sm' | 'md';
  showSignal?: boolean;
}

const SignalRow: React.FC<SignalRowProps> = ({
  symbol,
  price,
  change_percent,
  previousClose,
  signal,
  strength,
  confidence,
  size = 'sm',
  showSignal = true,
}) => {
  const navigate = useNavigate();
  const isStreaming = symbol.endsWith('.FOREX') || symbol.endsWith('.CC');
  const livePrice = useRealtimePrice(isStreaming ? symbol : '');

  // Determine currency based on exchange
  const isJSE = symbol.endsWith('.JSE');
  const currency = isJSE ? 'R' : '$';

  // Use live price if available, otherwise fall back to static price
  const displayPrice = livePrice.isLive && livePrice.price ? livePrice.price : price;

  // Calculate change percentage from previous close for accuracy
  // This matches broker calculations (change from yesterday's close, not today's open)
  const displayChange = livePrice.isLive && livePrice.price && previousClose
    ? ((livePrice.price - previousClose) / previousClose) * 100
    : change_percent;

  return (
    <div
      onClick={() => navigate(`/asset/${symbol}`)}
      className="flex items-center justify-between py-2 px-2 -mx-2 border-b border-slate-700/50 last:border-0 hover:bg-slate-700/30 rounded cursor-pointer transition-colors"
    >
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded bg-slate-700/50 flex items-center justify-center text-xs font-mono text-teal-400">
          {symbol.slice(0, 3)}
        </div>
        <div>
          <div className="flex items-center gap-2">
            <p className="font-medium text-sm">{symbol}</p>
            {livePrice.isLive && (
              <span className="flex items-center gap-1 px-1.5 py-0.5 bg-emerald-500/10 border border-emerald-500/30 rounded text-[10px] font-semibold text-emerald-400 uppercase tracking-wide">
                <span className="w-1 h-1 bg-emerald-500 rounded-full animate-pulse" />
                LIVE
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500">
            {currency} {displayPrice.toLocaleString()}
            <span className={displayChange >= 0 ? 'text-emerald-400' : 'text-red-400'}>
              {' '}{displayChange >= 0 ? '+' : ''}{displayChange.toFixed(2)}%
            </span>
          </p>
        </div>
      </div>
      {showSignal ? (
        <SignalBadge
          signal={signal}
          strength={strength}
          confidence={confidence}
          size={size}
        />
      ) : (
        <span className="px-2 py-1 rounded text-xs font-medium whitespace-nowrap bg-slate-700/50 text-slate-300">
          Signal hidden
        </span>
      )}
    </div>
  );
};

export default SignalRow;
